import React from "react";
import {
  Users, Calendar, Tag, Gift, Box, BookOpen, FileText, Image as ImageIcon, Workflow, Share2,
  MessageCircle, Sparkles, Receipt, CreditCard, Coins,
} from "lucide-react";
import { TABS, MONEY_COL_REM, valueKey } from "../globalDataTabs";
import { Text, Money, DateText, Badge, LongText, Ref } from "../globalDataCells";
import { statusChip, clientTxKindLabel, isKnownKind } from "../clientTxStatus";

/**
 * The columns of the fifteen Tenant 360 drill-down sections
 * (docs/auth-and-admin/super-admin-s7-tenant360-contract.md section 1.4).
 *
 * A section the Global Data page already lists reuses that tab's columns by reference, minus
 * the "practice" column: the page is already one practice. The five sections Global Data has
 * no tab for define theirs here. Every row shape is the backend mapper's
 * (`modules/global_data/sections.py`); nothing here guesses a field name.
 *
 * A column is `{ id, headerKey, width (rem), sort?, render(row, ctx) }`, `ctx` = `{ t, lang, loc }`.
 */

/**
 * The widest a section's columns may sum to. The drill-down's table area at a 1440px viewport
 * measured 1014px (63.4rem): the section card's padding and borders take what Global Data's 68rem
 * area does not lose (globalDataTabs MAX_TAB_WIDTH_REM). Above the area the table scrolls
 * sideways inside its card and clips its last column; the budget keeps a headroom below the area
 * like Global Data's (64rem in 68rem).
 */
export const MAX_SECTION_WIDTH_REM = 60;

const col = (id, headerKey, width, render, sort) => ({ id, headerKey, width, render, ...(sort ? { sort } : {}) });
const createdCol = (width = 7.5) => col("created", "gdv.h.created", width, (r, { lang }) => <DateText value={r.created_at} lang={lang} />, "created_at");
const updatedCol = (width = 7.5) => col("updated", "gdv.h.updated", width, (r, { lang }) => <DateText value={r.updated_at} lang={lang} />, "updated_at");

/** The i18n key of one value of one of this file's closed vocabularies. */
const vKey = (group, value) => `t360s.v.${group}.${value}`;
const vocab = (group, value, t, tone) => <Badge value={value} labelKey={vKey(group, value)} t={t} tone={tone} />;

/** A Global Data tab's columns, without its practice column. */
const sharedColumns = (tabKey) => TABS.find((x) => x.key === tabKey).columns.filter((c) => c.id !== "tenant");
const sharedSort = (tabKey) => TABS.find((x) => x.key === tabKey).defaultSort;

const PRODUCT_COLUMNS = [
  col("name", "gdv.h.name", 15.5, (r, { loc }) => <Text value={loc(r, "name")} className="font-medium" />),
  // `type` is how the product is sold (online checkout or affiliate link), `product_type` what
  // it is (physical or digital): two headers that must read differently in every language.
  col("type", "t360s.h.salesChannel", 8, (r, { t }) => vocab("productType", r.type, t)),
  col("nature", "t360s.h.nature", 8, (r, { t }) => vocab("productNature", r.product_type, t)),
  col("price", "gdv.h.price", MONEY_COL_REM, (r) => <Money value={r.price} />, "price_cents"),
  col("status", "gdv.h.status", 6, (r, { t }) => <Badge value={r.active ? "active" : "inactive"} labelKey={valueKey("programs", r.active ? "active" : "inactive")} t={t} tone={r.active ? "good" : undefined} />),
  col("stock", "t360s.h.stock", 7, (r, { t }) => vocab("stock", r.in_stock ? "in" : "out", t, r.in_stock ? "good" : "warn")),
  createdCol(),
];

const AUTOMATION_COLUMNS = [
  col("name", "gdv.h.name", 16, (r, { lang }) => <Text value={r.system_names?.[lang] || r.name} className="font-medium" />),
  col("trigger", "t360s.h.trigger", 14, (r, { t }) => vocab("trigger", r.trigger_type, t)),
  col("status", "gdv.h.status", 6.5, (r, { t }) => vocab("sequence", r.status, t, r.status === "active" ? "good" : undefined)),
  col("steps", "t360s.h.steps", 5, (r) => <span className="tabular-nums text-slate-500">{r.steps ?? 0}</span>),
  updatedCol(),
  createdCol(),
];

const SOCIAL_COLUMNS = [
  col("platforms", "t360s.h.platforms", 10, (r) => <Text value={Array.isArray(r.platforms) && r.platforms.length ? r.platforms.join(", ") : null} className="text-slate-500" />),
  col("content", "t360s.h.content", 21.5, (r, { t }) => <LongText value={r.content} t={t} />),
  col("status", "gdv.h.status", 7, (r, { t }) => vocab("social", r.status, t, r.status === "published" ? "good" : undefined)),
  col("scheduled", "t360s.h.scheduled", 9, (r, { lang }) => <DateText value={r.scheduled_at} lang={lang} withTime />, "scheduled_at"),
  col("image", "t360s.h.image", 5, (r, { t }) => vocab("image", r.has_image ? "yes" : "no", t)),
  createdCol(),
];

const AI_SESSION_COLUMNS = [
  col("context", "t360s.h.context", 8, (r, { t }) => vocab("context", r.context, t)),
  col("visitor", "t360s.h.visitor", 14, (r) => <Text value={r.visitor_name} className="font-medium" />),
  col("email", "gdv.h.email", 18, (r) => <Text value={r.visitor_email} className="text-slate-500" />),
  createdCol(),
];

const CLIENT_TRANSACTION_COLUMNS = [
  col("reference", "t360s.h.reference", 9, (r) => <Ref value={r.reference} />),
  col("kind", "gdv.h.type", 7.5, (r, { t }) => (
    <span title={isKnownKind(r.kind) ? undefined : (r.kind || undefined)}>{clientTxKindLabel(r.kind, t)}</span>
  )),
  col("email", "gdv.h.email", 11.5, (r) => <Text value={r.email} className="text-slate-500" />),
  col("amount", "gdv.h.amount", MONEY_COL_REM, (r) => <Money value={r.amount} />),
  col("refunded", "t360s.h.refunded", MONEY_COL_REM, (r) => <Money value={r.refunded} />),
  col("status", "gdv.h.status", 8.5, (r, { t }) => {
    const chip = statusChip(r, t);
    return <span className={`px-2 py-0.5 rounded text-xs ${chip.cls}`} title={chip.title}>{chip.label}</span>;
  }),
  createdCol(),
];

/**
 * The fifteen sections, in the order of the page's tiles and of the backend registry
 * (`modules/global_data/sections.py SECTIONS`).
 *
 * labelKey: the tile / heading i18n key   defaultSort: the wire sort the first read uses
 */
export const SECTIONS = [
  { key: "crm", labelKey: "t360.sec.crm", icon: Users, defaultSort: sharedSort("customers"), columns: sharedColumns("customers") },
  { key: "bookings", labelKey: "t360.sec.bookings", icon: Calendar, defaultSort: sharedSort("bookings"), columns: sharedColumns("bookings") },
  { key: "promos", labelKey: "t360.sec.promos", icon: Tag, defaultSort: sharedSort("promos"), columns: sharedColumns("promos") },
  { key: "giftcards", labelKey: "t360.sec.giftcards", icon: Gift, defaultSort: sharedSort("giftcards"), columns: sharedColumns("giftcards") },
  { key: "products", labelKey: "t360.sec.products", icon: Box, defaultSort: "-created_at", columns: PRODUCT_COLUMNS },
  { key: "programs", labelKey: "t360.sec.programs", icon: BookOpen, defaultSort: sharedSort("programs"), columns: sharedColumns("programs") },
  { key: "blog", labelKey: "t360.sec.blog", icon: FileText, defaultSort: sharedSort("blog"), columns: sharedColumns("blog") },
  { key: "media", labelKey: "t360.sec.media", icon: ImageIcon, defaultSort: sharedSort("media"), columns: sharedColumns("media") },
  { key: "automation", labelKey: "t360.sec.automation", icon: Workflow, defaultSort: "-created_at", columns: AUTOMATION_COLUMNS },
  { key: "social_posts", labelKey: "t360.sec.social_posts", icon: Share2, defaultSort: "-created_at", columns: SOCIAL_COLUMNS },
  { key: "ai_sessions", labelKey: "t360.sec.ai_sessions", icon: MessageCircle, defaultSort: "-created_at", columns: AI_SESSION_COLUMNS },
  { key: "insights", labelKey: "t360.sec.insights", icon: Sparkles, defaultSort: sharedSort("insights"), columns: sharedColumns("insights") },
  { key: "invoices", labelKey: "t360.sec.invoices", icon: Receipt, defaultSort: sharedSort("invoices"), columns: sharedColumns("invoices") },
  // "transactions" = the SaaS ledger (what this practice pays Solunary), the Global Data
  // "billing" tab. "client_transactions" = what it collects from ITS clients. Both reach the
  // wire as `{cents, currency}`, so their figures are formatted, never compared raw.
  { key: "transactions", labelKey: "t360.sec.transactions", icon: CreditCard, defaultSort: sharedSort("billing"), columns: sharedColumns("billing") },
  { key: "client_transactions", labelKey: "t360.sec.clientTransactions", icon: Coins, defaultSort: "-created_at", columns: CLIENT_TRANSACTION_COLUMNS },
];

export const sectionByKey = (key) => SECTIONS.find((s) => s.key === key);

/** Every closed vocabulary this file labels: group -> values (one i18n key per value, per language). */
export const SECTION_VALUE_GROUPS = {
  productType: ["stripe", "affiliate"],
  productNature: ["physical", "digital"],
  stock: ["in", "out"],
  sequence: ["draft", "active"],
  trigger: [
    "new_client", "booking_exists", "booking_completed", "purchase_completed", "no_activity",
    "pack_balance_hits", "tag_added", "nth_session_completed", "review_moment",
    "pathway_progress", "pathway_completed",
  ],
  social: ["draft", "scheduled", "published"],
  image: ["yes", "no"],
  context: ["patient", "admin"],
};
